// Утилиты для отслеживания событий и конверсий

declare global {
  interface Window {
    trackGoal?: (goalName: string, params?: Record<string, unknown>) => void;
    ym?: (id: number, method: string, ...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Отслеживание целей в Яндекс.Метрике и Google Analytics
 */
export const trackGoal = (goalName: string, params?: Record<string, unknown>) => {
  // Добавляем UTM-параметры к каждой цели
  const utmParams = getSavedUTMParams();
  const enrichedParams = { ...params, ...utmParams };

  // Яндекс.Метрика
  if (typeof window.trackGoal === 'function') {
    window.trackGoal(goalName, enrichedParams);
  }
  
  // Google Analytics
  if (typeof window.gtag === 'function') {
    window.gtag('event', goalName, enrichedParams);
  }
  
  // Консоль для отладки
  console.log('Goal tracked:', goalName, enrichedParams);
};

/**
 * Отслеживание источника трафика при входе на сайт
 */
export const trackTrafficSource = () => {
  const utmParams = getUTMParams();
  const referrer = document.referrer;
  
  // Определяем источник трафика
  let trafficSource = 'direct';
  
  if (utmParams.utm_source) {
    trafficSource = utmParams.utm_source;
  } else if (referrer) {
    if (referrer.includes('google')) trafficSource = 'google_organic';
    else if (referrer.includes('yandex')) trafficSource = 'yandex_organic';
    else if (referrer.includes('facebook')) trafficSource = 'facebook';
    else if (referrer.includes('instagram')) trafficSource = 'instagram';
    else trafficSource = 'referral';
  }
  
  trackGoal('page_visit', {
    traffic_source: trafficSource,
    referrer: referrer,
    page: window.location.pathname
  });
};

/**
 * Отслеживание клика по телефону
 */
export const trackPhoneClick = (phone: string) => {
  trackGoal('phone_click', { phone });
};

/**
 * Отслеживание клика по Telegram
 */
export const trackTelegramClick = (url: string) => {
  trackGoal('telegram_click', { url });
};

/**
 * Отслеживание клика по Instagram
 */
export const trackInstagramClick = (url: string) => {
  trackGoal('instagram_click', { url });
};

/**
 * Отслеживание отправки формы
 */
export const trackFormSubmit = (formType: string, data?: Record<string, unknown>) => {
  trackGoal('form_submit', { formType, ...data });
};

/**
 * Отслеживание успешной отправки формы
 */
export const trackFormSuccess = (formType: string, data?: Record<string, unknown>) => {
  trackGoal('form_success', { formType, ...data });
};

/**
 * Отслеживание просмотра каталога
 */
export const trackCatalogView = (category: string) => {
  trackGoal('catalog_view', { category });
};

/**
 * Отслеживание клика по кнопке "Узнать цену"
 */
export const trackPriceClick = (carModel: string, category: string) => {
  trackGoal('price_click', { carModel, category });
};

/**
 * Отслеживание добавления в избранное
 */
export const trackFavoriteAdd = (carModel: string) => {
  trackGoal('favorite_add', { carModel });
};

/**
 * Отслеживание клика по категории
 */
export const trackCategoryClick = (category: string) => {
  trackGoal('category_click', { category });
};

/**
 * Отслеживание скролла страницы
 */
export const trackPageScroll = (percentage: number) => {
  if (percentage === 25 || percentage === 50 || percentage === 75 || percentage === 100) {
    trackGoal('page_scroll', { percentage });
  }
};

/**
 * Отслеживание времени на странице
 */
export const trackTimeOnPage = (seconds: number) => {
  if (seconds === 30 || seconds === 60 || seconds === 120 || seconds === 300) {
    trackGoal('time_on_page', { seconds });
  }
};

/**
 * Получение UTM-меток из URL
 */
export const getUTMParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
    const value = params.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });
  
  return utmParams;
};

/**
 * Сохранение UTM-меток в localStorage
 */
export const saveUTMParams = () => {
  const utmParams = getUTMParams();
  if (Object.keys(utmParams).length > 0) {
    localStorage.setItem('utm_params', JSON.stringify(utmParams));
    localStorage.setItem('utm_timestamp', Date.now().toString());
    
    // Отслеживаем приход с рекламы
    trackGoal('utm_visit', utmParams);
  }
};

/**
 * Получение сохраненных UTM-меток
 */
export const getSavedUTMParams = (): Record<string, string> => {
  const saved = localStorage.getItem('utm_params');
  const timestamp = localStorage.getItem('utm_timestamp');
  
  // UTM-метки действуют 30 дней
  if (saved && timestamp) {
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    if (parseInt(timestamp) > thirtyDaysAgo) {
      return JSON.parse(saved);
    } else {
      // Удаляем устаревшие UTM-метки
      localStorage.removeItem('utm_params');
      localStorage.removeItem('utm_timestamp');
    }
  }
  
  return {};
};

/**
 * Получение информации об источнике трафика для форм
 */
export const getTrafficSourceInfo = (): Record<string, string> => {
  const utmParams = getSavedUTMParams();
  const currentUTM = getUTMParams();
  
  return {
    ...utmParams,
    ...currentUTM,
    referrer: document.referrer || '',
    landing_page: window.location.pathname,
    user_agent: navigator.userAgent
  };
};

/**
 * Отслеживание конверсий по рекламным кампаниям
 */
export const trackAdConversion = (conversionType: string, value?: number) => {
  const trafficInfo = getTrafficSourceInfo();
  
  trackGoal('ad_conversion', {
    conversion_type: conversionType,
    conversion_value: value || 0,
    ...trafficInfo
  });
  
  // Дополнительное отслеживание для Яндекс.Директ
  if (trafficInfo.utm_source === 'yandex' && trafficInfo.utm_medium === 'cpc') {
    trackGoal('yandex_direct_conversion', {
      campaign: trafficInfo.utm_campaign,
      keyword: trafficInfo.utm_term,
      ad_id: trafficInfo.utm_content,
      conversion_type: conversionType
    });
  }
};

/**
 * Отслеживание показа формы подбора авто
 */
export const trackQuizFormView = () => {
  trackGoal('quiz_form_view', getTrafficSourceInfo());
};

/**
 * Отслеживание показа формы осмотра
 */
export const trackInspectionFormView = () => {
  trackGoal('inspection_form_view', getTrafficSourceInfo());
};

/**
 * Отслеживание клика по CTA кнопке
 */
export const trackCTAClick = (ctaType: string, ctaText: string) => {
  trackGoal('cta_click', {
    cta_type: ctaType,
    cta_text: ctaText,
    ...getTrafficSourceInfo()
  });
};

/**
 * Отслеживание просмотра каталога автомобилей
 */
export const trackCatalogSectionView = (category: string) => {
  trackGoal('catalog_section_view', {
    category,
    ...getTrafficSourceInfo()
  });
};

/**
 * Отслеживание клика по категории авто
 */
export const trackCategoryCardClick = (category: string, destination: string) => {
  trackGoal('category_card_click', {
    category,
    destination,
    ...getTrafficSourceInfo()
  });
};

/**
 * Отслеживание просмотра блока "О компании"
 */
export const trackAboutSectionView = () => {
  trackGoal('about_section_view', getTrafficSourceInfo());
};

/**
 * Отслеживание просмотра блока преимуществ
 */
export const trackFeaturesSectionView = () => {
  trackGoal('features_section_view', getTrafficSourceInfo());
};

/**
 * Отслеживание просмотра отзывов
 */
export const trackTestimonialsSectionView = () => {
  trackGoal('testimonials_section_view', getTrafficSourceInfo());
};

/**
 * Получение параметра кампании из URL
 */
export const getCampaignParam = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_campaign');
};

/**
 * Проверка, пришел ли пользователь с конкретной рекламной кампании
 */
export const isFromCampaign = (campaignName: string): boolean => {
  const savedUTM = getSavedUTMParams();
  const currentUTM = getUTMParams();
  
  return savedUTM.utm_campaign === campaignName || currentUTM.utm_campaign === campaignName;
};
